import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trash2, Minus, Plus, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import PaypalButton from '../components/PaypalButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', notes: '' });
  // Prefer ?from=slug query (user came from a shop) over slug inferred from cart items
  const fromShopSlug = searchParams.get('from') || '';
  const [shopSlugFromCart, setShopSlugFromCart] = useState(null);
  const shopSlug = fromShopSlug || shopSlugFromCart;
  const shopHome = shopSlug ? `/negozio/${shopSlug}` : null;

  const fetchCart = async () => {
    if (!user) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/cart`, { withCredentials: true });
      setCart(data);
      // derive shop slug from first cart item's product for PayPal config
      const firstProductId = data?.items?.[0]?.product_id;
      if (firstProductId) {
        try {
          const { data: prod } = await axios.get(`${API}/products/${firstProductId}`);
          if (prod?.shop_slug) setShopSlugFromCart(prod.shop_slug);
        } catch (_) {}
      } else {
        setShopSlugFromCart(null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCart(); }, [user]);

  useEffect(() => {
    if (user) {
      setForm(prev => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email
      }));
    }
  }, [user]);

  const updateQuantity = async (itemId, quantity) => {
    try {
      if (quantity <= 0) {
        await axios.delete(`${API}/cart/${itemId}`, { withCredentials: true });
      } else {
        await axios.put(`${API}/cart/${itemId}`, { quantity }, { withCredentials: true });
      }
      fetchCart();
    } catch (e) {
      toast.error('Errore aggiornamento carrello');
    }
  };

  const removeItem = async (itemId) => {
    try {
      await axios.delete(`${API}/cart/${itemId}`, { withCredentials: true });
      fetchCart();
    } catch (e) {
      toast.error('Errore rimozione');
    }
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.address) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    setCheckingOut(true);
    try {
      const { data } = await axios.post(`${API}/orders/checkout`, form, { withCredentials: true });
      toast.success(`Ordine ricevuto! Totale: ${data.total}€`);
      setCart({ items: [], total: 0 });
      setShowCheckout(false);
      navigate(`/tracking/${data.order_id}`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Errore durante il checkout';
      toast.error(typeof msg === 'string' ? msg : 'Errore checkout');
    } finally {
      setCheckingOut(false);
    }
  };

  const handleStripePayment = async () => {
    setCheckingOut(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        origin_url: window.location.origin,
        from_shop: shopSlug || ''
      }, { withCredentials: true });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Errore pagamento';
      toast.error(typeof msg === 'string' ? msg : 'Errore');
      setCheckingOut(false);
    }
  };

  if (!user) {
    return (
      <div data-testid="cart-page" className="pb-safe px-4 pt-8 text-center">
        {shopHome && (
          <button
            data-testid="cart-back-to-shop-empty"
            onClick={() => navigate(shopHome)}
            className="inline-flex items-center gap-1.5 text-xs text-[#5C554F] hover:text-[#C25934] mb-3 absolute left-4 top-4"
          >
            ← Torna al negozio
          </button>
        )}
        <div className="w-20 h-20 bg-[#F0EBE1] rounded-full flex items-center justify-center mx-auto mb-4">
          <ShoppingCart size={32} className="text-[#877E78]" />
        </div>
        <h2 className="text-xl font-semibold text-[#2A2624] mb-2" style={{ fontFamily: 'Outfit' }}>
          Accedi per il carrello
        </h2>
        <p className="text-[#877E78] text-sm mb-4">Effettua il login per aggiungere prodotti al carrello</p>
        <Button
          data-testid="cart-login-btn"
          onClick={() => navigate(shopSlug ? `/account?next=${encodeURIComponent(`/carrello?from=${shopSlug}`)}` : '/account')}
          className="bg-[#C25934] hover:bg-[#A64B2B] text-white rounded-full px-8 h-12"
        >
          Accedi
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="pb-safe px-4 pt-4">
      {shopHome && (
        <button
          data-testid="cart-back-to-shop"
          onClick={() => navigate(shopHome)}
          className="inline-flex items-center gap-1.5 text-xs text-[#5C554F] hover:text-[#C25934] mb-3"
        >
          ← Torna al negozio
        </button>
      )}
      <h1 className="text-2xl font-semibold text-[#2A2624] mb-4" style={{ fontFamily: 'Outfit' }}>
        Carrello
      </h1>

      {loading ? (
        <div className="space-y-3">
          {[1, 2].map(i => <div key={i} className="h-24 skeleton-shimmer rounded-xl" />)}
        </div>
      ) : cart.items.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-[#F0EBE1] rounded-full flex items-center justify-center mx-auto mb-4">
            <ShoppingCart size={32} className="text-[#877E78]" />
          </div>
          <h3 className="text-lg font-medium text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
            Carrello vuoto
          </h3>
          <p className="text-sm text-[#877E78] mt-1 mb-4">Aggiungi prodotti dal catalogo</p>
          <Button
            data-testid="browse-products-btn"
            onClick={() => navigate(shopHome || '/catalogo')}
            className="bg-[#C25934] hover:bg-[#A64B2B] text-white rounded-full px-6 h-12"
          >
            {shopHome ? 'Torna al negozio' : 'Vai al Catalogo'}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {cart.items.map(item => (
            <CartItem key={item.id} item={item} onUpdate={updateQuantity} onRemove={removeItem} />
          ))}

          {/* Total */}
          <div className="bg-white rounded-2xl border border-[#E8E3D9] p-4">
            <div className="flex justify-between items-center">
              <span className="text-[#5C554F] font-medium">Totale</span>
              <span className="text-2xl font-bold text-[#C25934]" style={{ fontFamily: 'Outfit' }}>
                {cart.total?.toFixed(2)} &euro;
              </span>
            </div>
          </div>

          {!showCheckout ? (
            <Button
              data-testid="checkout-btn"
              onClick={() => setShowCheckout(true)}
              className="w-full h-14 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white text-base font-semibold"
              style={{ fontFamily: 'Outfit' }}
            >
              Procedi all'Ordine
            </Button>
          ) : (
            <form onSubmit={handleCheckout} className="bg-white rounded-2xl border border-[#E8E3D9] p-4 space-y-3">
              <h3 className="font-medium text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>Dati per l'ordine</h3>
              <Input
                data-testid="checkout-name"
                placeholder="Nome e Cognome *"
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="h-12 rounded-xl border-[#E8E3D9]"
                required
              />
              <Input
                data-testid="checkout-email"
                placeholder="Email *"
                type="email"
                value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                className="h-12 rounded-xl border-[#E8E3D9]"
                required
              />
              <Input
                data-testid="checkout-phone"
                placeholder="Telefono *"
                value={form.phone}
                onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                className="h-12 rounded-xl border-[#E8E3D9]"
                required
              />
              <textarea
                data-testid="checkout-address"
                placeholder="Indirizzo di consegna *"
                value={form.address}
                onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 rounded-xl border border-[#E8E3D9] bg-white text-sm resize-none outline-none focus:ring-2 focus:ring-[#C25934]"
                required
              />
              <Input
                data-testid="checkout-notes"
                placeholder="Note aggiuntive (opzionale)"
                value={form.notes}
                onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                className="h-12 rounded-xl border-[#E8E3D9]"
              />
              <Button
                data-testid="confirm-order-btn"
                type="submit"
                disabled={checkingOut}
                className="w-full h-14 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white text-base font-semibold"
              >
                {checkingOut ? 'Invio ordine...' : `Conferma Ordine - ${cart.total?.toFixed(2)} €`}
              </Button>
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#E8E3D9]"></div></div>
                <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-[#877E78]">oppure</span></div>
              </div>
              <Button
                data-testid="pay-stripe-btn"
                type="button"
                disabled={checkingOut}
                onClick={handleStripePayment}
                className="w-full h-14 rounded-full bg-[#635BFF] hover:bg-[#524DDB] text-white text-base font-semibold"
              >
                {checkingOut ? 'Reindirizzamento...' : `Paga con Carta o Klarna - ${cart.total?.toFixed(2)} €`}
              </Button>
              <div className="text-[11px] text-[#877E78] text-center mt-1.5">
                Carta · Apple/Google Pay · Klarna (Paga in 3 rate) · Bonifico SEPA
              </div>

              {shopSlug && (
                <div data-testid="paypal-section" className="pt-2">
                  <PaypalButton
                    shopSlug={shopSlug}
                    shippingInfo={form}
                    onSuccess={(order) => {
                      setCart({ items: [], total: 0 });
                      setShowCheckout(false);
                      if (order?.id) navigate(`/tracking/${order.id}${shopSlug ? `?from=${shopSlug}` : ''}`);
                    }}
                  />
                </div>
              )}
            </form>
          )}
        </div>
      )}
    </div>
  );
}

function CartItem({ item, onUpdate, onRemove }) {
  const [imgSrc, setImgSrc] = useState(null);

  useEffect(() => {
    if (!item.product_image) return;
    axios.get(`${API}/files/${item.product_image}`, { responseType: 'blob' })
      .then(res => setImgSrc(URL.createObjectURL(res.data)))
      .catch(() => {});
    return () => { if (imgSrc) URL.revokeObjectURL(imgSrc); };
  }, [item.product_image]);

  return (
    <div data-testid={`cart-item-${item.id}`} className="bg-white rounded-xl border border-[#E8E3D9] p-3 flex gap-3">
      <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#F0EBE1] flex-shrink-0">
        {imgSrc ? (
          <img src={imgSrc} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#877E78]">
            <ShoppingCart size={16} />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-sm text-[#2A2624] truncate">{item.product_title}</h4>
        <p className="text-[#C25934] font-bold text-sm">{item.product_price?.toFixed(2)} &euro;</p>
        {item.selected_variants && Object.keys(item.selected_variants).length > 0 && (
          <p className="text-[10px] text-[#877E78]">
            {Object.entries(item.selected_variants).map(([k, v]) => `${k}: ${v}`).join(' | ')}
          </p>
        )}
      </div>
      <div className="flex flex-col items-end justify-between">
        <button onClick={() => onRemove(item.id)} className="p-1 text-red-400 hover:text-red-600">
          <Trash2 size={14} />
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onUpdate(item.id, (item.quantity || 1) - 1)}
            className="w-7 h-7 rounded-full bg-[#F0EBE1] flex items-center justify-center"
          >
            <Minus size={12} />
          </button>
          <span className="text-sm font-medium w-5 text-center">{item.quantity || 1}</span>
          <button
            onClick={() => onUpdate(item.id, (item.quantity || 1) + 1)}
            className="w-7 h-7 rounded-full bg-[#F0EBE1] flex items-center justify-center"
          >
            <Plus size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}
